import { create } from "zustand";

import { tokenStorage } from "@/lib/storage/token";
import { AnalyticsService } from "@/services/analytics-service";
import { apiAuth } from "@/services/api-client/auth";
import type { AuthMetadata } from "@/services/api-client/auth/types";
import { mapAnalyticsToAuthMetadata } from "@/services/api-client/auth/utils";
import { apiUser } from "@/services/api-client/user";
import { subscriptionsKeys } from "@/services/api-hooks/subscriptions";
import { clearGetMeCache, updateGetMeCache } from "@/services/api-hooks/user/user.cache";
import { queryClient } from "@/services/query-client";
import type { AuthResponse, User } from "@/types/auth";

interface SignupDefaultParams {
  email: string;
  userName: string;
  password: string;
  metadata?: AuthMetadata;
  acceptMarketing: boolean;
}

interface SignupParams extends Omit<SignupDefaultParams, "userName"> {}
interface SignupLiteParams extends Omit<SignupDefaultParams, "password"> {}
interface LoginWithGoogleParams
  extends Pick<SignupDefaultParams, "metadata" | "acceptMarketing"> {
  code: string;
}

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<User>;
  magicLogin: (code: string) => Promise<User>;
  signup: (params: SignupParams) => Promise<User>;
  signupLite: (params: SignupLiteParams) => Promise<User>;
  setPassword: (password: string) => Promise<void>;
  loginWithGoogle: (params: LoginWithGoogleParams) => Promise<User>;
  logout: () => void;
  clearError: () => void;
}

const storedToken = tokenStorage.getToken();
const storedRefreshToken = tokenStorage.getRefreshToken();

const seedUser = (user: User) => {
  updateGetMeCache(queryClient, () => user);
};

const clearUser = () => {
  clearGetMeCache(queryClient);
  queryClient.removeQueries({ queryKey: subscriptionsKeys.all });
};

export const useAuthStore = create<AuthState>((set) => {
  const runAuthFlow = async (authCall: () => Promise<AuthResponse>) => {
    set({ isLoading: true, error: null });
    try {
      const resp = await authCall();
      tokenStorage.set(resp.access_token, resp.refresh_token);
      AnalyticsService.initializePuid(resp.access_token);

      const user = await apiUser.getUser();
      seedUser(user);

      set({
        token: resp.access_token,
        refreshToken: resp.refresh_token?.value || null,
        isAuthenticated: true,
        isLoading: false,
      });

      return user;
    } catch (err) {
      tokenStorage.clear();
      clearUser();
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : "Unknown error",
      });
      throw err;
    }
  };

  const retrieveAuthMetadata = (additionalMetadata?: AuthMetadata) => {
    return {
      ...mapAnalyticsToAuthMetadata(AnalyticsService.getAll()),
      ...additionalMetadata,
    };
  };

  return {
    token: storedToken,
    refreshToken: storedRefreshToken,
    isAuthenticated: !!storedToken,
    isLoading: false,
    error: null,
    login: (email, password) => runAuthFlow(() => apiAuth.login(email, password)),
    magicLogin: (code: string) => runAuthFlow(() => apiAuth.magicLogin(code)),
    signup: ({ email, password, metadata, acceptMarketing }) =>
      runAuthFlow(() =>
        apiAuth.register(
          email,
          password,
          retrieveAuthMetadata(metadata),
          acceptMarketing,
        ),
      ),
    signupLite: ({ email, userName, metadata, acceptMarketing }) =>
      runAuthFlow(() =>
        apiAuth.registerLite(
          email,
          userName,
          retrieveAuthMetadata(metadata),
          acceptMarketing,
        ),
      ),
    setPassword: async (password) => {
      set({ isLoading: true, error: null });
      try {
        await apiAuth.setPassword(password);
        set({ isLoading: false });
      } catch (err) {
        set({
          isLoading: false,
          error: err instanceof Error ? err.message : "Unknown error",
        });
        throw err;
      }
    },
    loginWithGoogle: ({ code, metadata, acceptMarketing }) =>
      runAuthFlow(() =>
        apiAuth.loginWithGoogle(code, retrieveAuthMetadata(metadata), acceptMarketing),
      ),

    logout: () => {
      tokenStorage.clear();
      clearUser();
      set({ token: null, refreshToken: null, isAuthenticated: false });
    },

    clearError: () => set({ error: null }),
  };
});
